import { FC } from 'react'
import cn from 'classnames'

import useMessenger from '@/hooks/useMessenger'

import { StoreContext } from '@/context/context'
import { IMessage, StoreContextType } from '@/context/types'

import TextType from '@/components/ContentTypes/TextType'
import ListType from '@/components/ContentTypes/ListType'
import ImageType from '@/components/ContentTypes/ImageType'
import QuickReply from '@/components/QuickReply'
import FAQCard from '@/components/ContentTypes/FAQCard'
import WorkOrderCard from '@/components/ContentTypes/WorkOrderCard'
import PropertyCard from '@/components/ContentTypes/PropertyCard'
import TicketForm from '@/components/ContentTypes/TicketForm'
import RedirectLink from '@/components/ContentTypes/RedirectLink'
import AgentAssigned from '@/components/ContentTypes/AgentAssigned'
import CategoriesCarousel from '@/components/ContentTypes/CategoriesCarousel'
import WorkOrderCarousel from '@/components/ContentTypes/WorkOrderCarousel'

import {
  AUTHOR_TYPES,
  CHANNEL_TYPES,
  CONTENT_TYPES,
  DIRECTION,
} from '@/context/constants'

import { formatDateRelative, capitalize } from '../../common/utils'

import './styles.css'

interface Props {
  message: IMessage
  isChatWidget: boolean
  context?: React.Context<StoreContextType | null>
}

const Message: FC<Props> = ({
  message,
  isChatWidget,
  context = StoreContext,
}) => {
  const { getLastConversationMessage, getConfig } = useMessenger(context)
  const isLast = getLastConversationMessage()?.id === message.id

  const getDirection = () => {
    const userExternalId = getConfig()?.userExternalId

    if (
      message.author.type === AUTHOR_TYPES.BUSINESS ||
      userExternalId !== message.author.userExternalId
    ) {
      return DIRECTION.LEFT
    }

    return DIRECTION.RIGHT
  }
  const direction = getDirection()

  const renderContentType = (type: string) => {
    switch (type) {
      case CONTENT_TYPES.MAIN_CAROUSEL:
        return (
          <CategoriesCarousel message={message} isChatWidget={isChatWidget} />
        )
      case CONTENT_TYPES.LIST:
        return <ListType message={message} direction={direction} />
      case CONTENT_TYPES.IMAGE:
        return <ImageType message={message} />
      case CONTENT_TYPES.TICKET_FORM:
        if (!isLast) return null
        return <TicketForm message={message} direction={direction} />
      case CONTENT_TYPES.QUICK_REPLY:
        if (!isLast) return null
        return <QuickReply message={message} />
      case CONTENT_TYPES.FAQ_CARD:
        return <FAQCard message={message} />
      case CONTENT_TYPES.WO_CAROUSEL:
        return <WorkOrderCarousel message={message} />
      case CONTENT_TYPES.WORK_ORDER_CARD:
        return <WorkOrderCard message={message} />
      case CONTENT_TYPES.PROPERTY_CARD:
        return <PropertyCard message={message} />
      case CONTENT_TYPES.REDIRECT_LINK:
        return <RedirectLink message={message} />
      case CONTENT_TYPES.AGENT_ASSIGNED:
        return <AgentAssigned message={message} />
      case CONTENT_TYPES.TEXT:
      default:
        return (
          <TextType message={message} direction={direction} context={context} />
        )
    }
  }

  const renderTimestamp = (message: IMessage) => {
    if (
      [
        CONTENT_TYPES.AGENT_ASSIGNED,
        CONTENT_TYPES.QUICK_REPLY,
        CONTENT_TYPES.TICKET_FORM,
      ].includes(message.content?.type)
    ) {
      return null
    }

    return (
      <div className={cn('pmui-text-xs pmui-pt-1.5 pmui-text-[#86888A]')}>
        {formatDateRelative(message.received)}
      </div>
    )
  }

  const renderSentVia = (message: IMessage) => {
    let sentVia = 'Sent via messages'
    if (message.metadata?.channel === CHANNEL_TYPES.EMAIL) {
      sentVia = 'Sent via email'
    } else if (message.metadata?.channel === CHANNEL_TYPES.SMS) {
      sentVia = 'Sent via sms'
    }

    return (
      <div className={cn('pmui-text-xs pmui-pt-1.5 pmui-text-[#86888A]')}>
        {sentVia}
      </div>
    )
  }

  const renderAuthorType = (message: IMessage) => {
    const { content, author } = message

    if (content.type !== CONTENT_TYPES.TEXT) {
      return null
    }

    return (
      <div
        className={cn(
          'pmui-flex pmui-items-center pmui-gap-x-2 pmui-text-xs pmui-font-semibold',
          {
            'pmui-justify-end': direction === DIRECTION.RIGHT,
          },
        )}
      >
        {author.type === AUTHOR_TYPES.BUSINESS
          ? 'Poplar Homes'
          : capitalize(author.type)}
      </div>
    )
  }

  const renderDeliveryReceipt = (message: IMessage) => {
    if (direction === DIRECTION.LEFT) {
      return null
    }

    return <div className="icon icon-req-complete pmui-pt-1.5 pmui-text-xl" />
  }

  return (
    <div className="pmui-mb-10 pmui-text-xs pmui-w-full">
      <div
        className={cn('pmui-flex pmui-w-full', {
          'pmui-justify-end pmui-mr-2': direction === DIRECTION.RIGHT,
        })}
      >
        <div className="pmui-w-full message-enter-animation">
          {renderAuthorType(message)}
          {renderContentType(message.content?.type)}
          {message.content.type === CONTENT_TYPES.TEXT && (
            <div
              className={cn(
                'pmui-flex pmui-items-center pmui-gap-2 pmui-text-xs ',
                {
                  'pmui-justify-end ': direction === DIRECTION.RIGHT,
                },
              )}
            >
              {renderDeliveryReceipt(message)}
              {renderTimestamp(message)}
              <div className="pmui-bg-pop-gray-200 pmui-w-1 pmui-h-1 pmui-mt-2 pmui-rounded-full" />
              {renderSentVia(message)}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default Message
